<script lang="ts"></script>

<script setup lang="ts">
import type { NavigationOptions, PaginationOptions, Swiper as SwiperType } from "swiper/types";

import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import { nextTick, ref } from "vue";

import { useCarousel } from "@/stores/carousel/carousel";
import BreezyWhite from "@/ui/icons/breezyWhite.vue";
import Close from "@/ui/icons/close.vue";
import SwiperArrow from "@/ui/icons/swiperArrow.vue";
import "swiper/swiper-bundle.css";
import "swiper/css/pagination";

const CarouselStore = useCarousel();

const prevEl = ref<HTMLElement>(null);
const nextEl = ref<HTMLElement>(null);

async function handleVideo(swiper: SwiperType) {
    await nextTick();
    const videos = swiper.el.querySelectorAll("video");
    videos.forEach((el) => {
        const parentSlide = el.closest(".swiper-slide");
        const isActive = parentSlide?.classList.contains("swiper-slide-active") ?? false;

        if (isActive) {
            el.play().catch(() => {});
        } else {
            el.pause();
            el.currentTime = 0;
        }
    });
}

const paginationOptions: PaginationOptions = {
    el: ".tiw__carousel_pagination",
    clickable: true,
    dynamicBullets: false,
    renderBullet: (_, className) => {
        return `<span class="${className}"></span>`;
    },
};

function onSwiper(swiper: SwiperType) {
    const navigation = swiper.params.navigation as NavigationOptions;
    navigation.prevEl = prevEl.value;
    navigation.nextEl = nextEl.value;
    handleVideo(swiper);
    swiper.navigation.init();
    swiper.navigation.update();
}
</script>

<template>
    <div data-component="carousel" v-if="CarouselStore.isShown" class="tiw__carousel" @click="CarouselStore.hide()">
        <BreezyWhite class="tiw__carousel_logo" @click.stop />
        <button class="tiw__carousel_close" @click="CarouselStore.hide()">
            <Close variant="basic" color="white" />
        </button>
        <Swiper
            :centered-slides="true"
            :breakpoints="{
                0: {
                    slidesPerView: 1,
                },
                992: {
                    slidesPerView: 3,
                },
            }"
            :space-between="0"
            :loop="false"
            :modules="[Navigation, Pagination]"
            :pagination="paginationOptions"
            @swiper="onSwiper"
            @slide-change="handleVideo"
        >
            <SwiperSlide
                v-for="slide in CarouselStore.slides"
                :key="slide.url"
                :zoom="true"
                @click.stop
            >
                <div class="tiw__carousel_container">
                    <video
                        v-if="slide.type === 'video'"
                        class="tiw__carousel_video"
                        muted
                        controls
                        width="100%"
                        height="100%"
                    >
                        <source :src="slide.url" type="video/webm" />
                    </video>
                    <img
                        v-else
                        class="tiw__carousel_img"
                        width="100%"
                        height="100%"
                        :src="slide.url"
                        alt="slide pictures"
                    />
                </div>
            </SwiperSlide>
            <button ref="prevEl" class="tiw__carousel_button tiw__carousel_button_prev" @click.stop>
                <SwiperArrow class="tiw__carousel_icon" />
            </button>
            <button ref="nextEl" class="tiw__carousel_button tiw__carousel_button_next" @click.stop>
                <SwiperArrow class="tiw__carousel_icon" />
            </button>
        </Swiper>
        <div class="tiw__carousel_pagination" />
    </div>
</template>

<style lang="scss" src="./carousel.scss"></style>
